import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
// import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import addToCart from "@tSurya/Handlers/AddToCartHandler/AddToCartHandler";
import ProductSelector, { ProductSelectorStyles } from "@tSurya/Overrides/Components/ProductSelector";
import { isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import { TYPOGRAPHY, COLORS } from "@tSurya/theme/design.surya";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    allSites = "allSites",
}

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    return {
        canOrderUpload: settingsCollection.orderSettings.canOrderUpload,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        orderUploadPageNavLink: getPageLinkByPageType(state, "OrderUploadPage"),
        quickOrderPageNavLink: getPageLinkByPageType(state, "QuickOrderPage"),
        allowQuickOrder: settingsCollection.orderSettings.allowQuickOrder,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart as any),
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.allSites]: boolean;
    };
}

export type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface QuickOrderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    linksGridItem?: GridItemProps;
    orderUploadLink?: LinkPresentationProps;
    orderMultipleItemsLink?: LinkPresentationProps;
    productSelector?: ProductSelectorStyles;
}

export const quickOrderStyles: QuickOrderStyles = {
    container: {
        gap: 15,
    },
    titleGridItem: { width: [12, 5, 5, 5, 5] },
    titleText: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
        `,
    },
    linksGridItem: {
        width: [12, 7, 7, 7, 7],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 20px;
                        padding-top: 0;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-top: 20px;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-top: 20px;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-top: 20px;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-top: 20px;
                    `,
                ])}
        `,
    },
    orderMultipleItemsLink: {
        css: css`
            margin-left: 20px;
        `,
    },
    productSelector: {
        container: {
            gap: 30,
            css: css`
                margin-bottom: 10px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                margin-top: 24px;
                            `,
                        ],
                        "max",
                    )}

                label[class^="TypographyStyle"] {
                    font-family: ${TYPOGRAPHY.fontFamily.title};
                    font-weight: ${TYPOGRAPHY.weight.baseBold};
                    color: ${COLORS.primary.base};
                    font-size: ${TYPOGRAPHY.size.base};
                    padding-bottom: 4px;
                }
            `,
        },
        searchGridItem: {
            width: [8, 8, 5, 5, 5],
        },
        qtyGridItem: { width: [4, 4, 2, 2, 2] },
        unitOfMeasureGridItem: { width: [12, 12, 3, 2, 1] },
        buttonGridItem: {
            width: [12, 12, 3, 3, 3],
        },
        selectButton: {
            css: css`
                width: fit-content;
                align-self: flex-end;
                border: 1px solid ${COLORS.primary.base};
                color: ${COLORS.primary.base};
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: 16px !important;
                
                &:not(:disabled) {
                    border: 1px solid ${COLORS.primary.base};

                    span {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        font-size: 16px !important;
                        color: ${COLORS.primary.base};
                    }

                }

                &:hover:not(:disabled) {
                    color: ${COLORS.darkmagenta} !important;
                    border-color: ${COLORS.darkmagenta} !important;
                    background-color: transparent !important;

                    span {
                        color: ${COLORS.darkmagenta} !important;
                    }
                }

                &:disabled {
                    background-color: #FFFFFF !important;
                    border-color: #D0D0D0 !important;
                    color: #9E9E9E !important;
                    cursor: not-allowed;

                    span, span[class^="TypographyStyle"] {
                        color: #9E9E9E !important;
                        font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                        font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
                        font-size: 16px !important;
                    }
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                                margin-top: 16px;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
        clearIcon: {
            color: "#545454",
            css: css`
                position: absolute;
                right: 50px;
                cursor: pointer;

                ${() => css`
                    top: ${isMgbwWebsite() ? "64px" : "56px"};
                `}

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                top: ${isMgbwWebsite() ? "49px" : "42px"};
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
};

export const SurCartQuickOrder = ({
    canOrderUpload,
    showAddToCartConfirmationDialog,
    orderUploadPageNavLink,
    quickOrderPageNavLink,
    toaster,
    addToCart,
    allowQuickOrder,
    fields,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>("");

    if (!allowQuickOrder) {
        return null;
    }

    const styles = useMergeStyles("quickOrder", quickOrderStyles);

    const addProductToCart = async (productInfo: ProductInfo, product: ProductModel) => {
        if (
            !product.quoteRequired &&
            !product.allowZeroPricing &&
            productInfo.pricing &&
            getUnitNetPrice(productInfo.pricing, 1).price === 0
        ) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const cartline = (await addToCart({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
            isRollPadService: false,
            onError: () => {
                setErrorMessage(siteMessage("Product_NotFound"));
            },
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            toaster.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
        }
    };

    return (
        <>
            <GridContainer {...styles.container}>
                {/* <GridItem {...styles.titleGridItem}>
                </GridItem>
                <GridItem {...styles.linksGridItem}>
                    {canOrderUpload && (
                        <Link
                            {...styles.orderUploadLink}
                            href={orderUploadPageNavLink ? orderUploadPageNavLink.url : undefined}
                        >
                            {translate("Upload an Order")}
                        </Link>
                    )}
                    <Link
                        {...styles.orderMultipleItemsLink}
                        href={quickOrderPageNavLink ? quickOrderPageNavLink.url : undefined}
                    >
                        {translate("Order Multiple Items")}
                    </Link>
                </GridItem> */}
            </GridContainer>
            <ProductSelector
                selectButtonTitle={translate("Add to Cart")}
                onSelectProduct={addProductToCart}
                allSites={fields.allSites}
                productIsConfigurableMessage={siteMessage("QuickOrder_CannotOrderConfigurable")}
                productIsUnavailableMessage={siteMessage("QuickOrder_ProductIsUnavailable")}
                customErrorMessage={errorMessage}
                extendedStyles={styles.productSelector}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(SurCartQuickOrder)),
    definition: {
        group: "Basic",
        icon: "cart-shopping-fast",
        displayName: "SurCartQuickOrder",
        fieldDefinitions: [
            {
                name: fields.allSites,
                displayName: "Unified Search (All Sites)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tooltip: "When enabled, product search returns results from all sites. When disabled, only the current site is searched.",
            },
        ],
    },
};

export default widgetModule;